import React, { memo, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import DynamicDropDownlist from '../../../AddNewVehicles/components/DynamicDropDownlist';
import { searchClient } from '../../../../actions/formAction';
import PropTypes from 'prop-types';

// Helpers
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const norm = path.replace(/\[(\w+)\]/g, '.$1').replace(/\['([^']+)'\]/g, '.$1');
  return norm.split('.').filter(Boolean).reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const formatValue = (v) => {
  if (v == null) return '-';
  if (v instanceof Date) return v.toLocaleDateString();
  if (typeof v === 'object') {
    if (typeof v.name === 'string') return v.name;
    if (typeof v.number === 'string' || typeof v.number === 'number') return String(v.number);
    return '-';
  }
  return String(v);
};

function ExporterInfo({ disabled = false, basePath = '', compact = false, title }) {
  const { t } = useTranslation();
  const formik = useFormikContext();

  const exporterPath = basePath ? `${basePath}.exporter` : 'exporter';
  const clientExporterPath = basePath ? `${basePath}.clientExporter` : 'clientExporter';
  const exporter = getByPath(formik.values, exporterPath);

  const handleOnChangeClient = useCallback(
    (newValue) => {
      formik.setFieldValue(clientExporterPath, newValue);
      formik.setFieldValue(exporterPath, newValue?.exporter || null);
    },
    [formik, clientExporterPath, exporterPath]
  );

  const details = useMemo(
    () =>
      [
        { label: t('Phone Number'), value: exporter?.phoneNumber },
        { label: t('Email'), value: exporter?.email },
        { label: t('Address'), value: [exporter?.street, exporter?.city, exporter?.country].filter(Boolean).join(', ') },
      ].filter((d) => d.value != null && d.value !== ''),
    [exporter, t]
  );

  return (
    <Box sx={{ pt: compact ? 0 : 4 }}>
      {!compact && <PageHeader title={title || t('Exporter Information')} />}
      {compact && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0B3C5D' }}>
          {title || t('Exporter')}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'stretch', md: 'flex-start' },
          px: { xs: 0, md: 3 },
          pt: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 380, md: 360 } }}>
          <DynamicDropDownlist
            label="Exporter Name"
            formik={formik}
            fieldName={clientExporterPath}
            disabled={disabled || !!getByPath(formik.values, basePath ? `${basePath}.primaryClient.exporter` : 'primaryClient.exporter')}
            loadingKey="isLoadingClientData"
            dataKey="client"
            fetchAction={searchClient}
            errorKey={exporterPath}
            onChange={handleOnChangeClient}
            extraQueryParams={{ withExporter: true }}
          />
        </Box>
      </Box>

      {exporter && (
        <Box sx={{ px: { xs: 0, md: 3 }, mt: { xs: 2, md: 3 } }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5eaef', borderRadius: 2, bgcolor: '#fff' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(auto-fill,minmax(200px,1fr))', md: 'repeat(3,1fr)' },
                gap: 2,
              }}
            >
              {details.map((d) => (
                <Box key={d.label} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, whiteSpace: 'nowrap' }}>
                    {d.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#0B3C5D',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={formatValue(d.value)}
                  >
                    {formatValue(d.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: { xs: 2, md: 3 } }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('Name')}
                </Typography>
                <Typography variant="h6" sx={{ m: 0, color: '#0B3C5D' }}>
                  {formatValue(exporter?.name)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('Export Number')}
                </Typography>
                <Typography variant="h6" sx={{ m: 0, color: '#0B3C5D' }}>
                  {formatValue(exporter?.exportNumber)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

ExporterInfo.propTypes = {
  disabled: PropTypes.bool,
  basePath: PropTypes.string,
  compact: PropTypes.bool,
  title: PropTypes.string,
};

export default memo(ExporterInfo);
